import React, { useEffect, useState, useCallback } from 'react';
import { Send } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../lib/api';
import Pagination from '../../components/Pagination';
import { Segmented, Stat, StatGrid, Tag, TableCard, Th, Td, trClass, TableMessage, StickyPager, fmtDate, fmtDateTime } from './ui';
import { friendlyError } from '../../lib/errorReporting';

// Leads — emails left on the landing page ("déjanos tu correo"). Shows who
// later created an account, and sends the current feature announcement to
// those who didn't (and haven't unsubscribed), once per campaign.

const LIMIT = 25;

const FILTERS = [
  { key: '', label: 'Todos' },
  { key: 'pending', label: 'Sin cuenta' },
  { key: 'converted', label: 'Crearon cuenta' },
];

export default function AdminLeads() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = useCallback(async (p, f) => {
    setLoading(true);
    try {
      const res = await api.getAdminLeads({ page: p, limit: LIMIT, status: f || undefined });
      setItems(res.items);
      setTotal(res.total);
      setStats(res.stats);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(page, filter); }, [page, filter, load]);

  const handleAnnounce = async () => {
    try {
      const preview = await api.announceToLeads(true);
      if (!preview.would_send) {
        toast.message('No hay leads pendientes para este anuncio');
        return;
      }
      if (!window.confirm(
        `¿Enviar el anuncio de novedades (escaneo de recibos y Metas) a ${preview.would_send} ${preview.would_send === 1 ? 'lead' : 'leads'} sin cuenta?\n\n${preview.emails.join('\n')}`
      )) return;
      setSending(true);
      const r = await api.announceToLeads(false);
      if (r.failed) toast.error(`${r.sent} enviados, ${r.failed} fallaron — revisa Logs → Correos`);
      else toast.success(`Anuncio enviado a ${r.sent} ${r.sent === 1 ? 'lead' : 'leads'}`);
      load(page, filter);
    } catch (e) {
      toast.error(friendlyError(e, 'No se pudo enviar el anuncio'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6 pb-2" data-testid="admin-leads">
      {stats && (
        <StatGrid>
          <Stat label="Leads" value={stats.total} sub="Dejaron su correo en la landing" />
          <Stat label="Crearon cuenta" value={stats.converted} dot="#34d399"
            sub={stats.total ? `${Math.round((stats.converted / stats.total) * 100)}% de conversión` : undefined} />
          <Stat label="Sin cuenta" value={stats.without_account} dot="#fbbf24" />
          <Stat label="Se dieron de baja" value={stats.unsubscribed} />
        </StatGrid>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <Segmented options={FILTERS} value={filter} onChange={(f) => { setFilter(f); setPage(1); }} />
        <button
          type="button"
          onClick={handleAnnounce}
          disabled={sending || !stats?.announce_pending}
          data-testid="leads-announce-btn"
          className="h-10 px-4 rounded-full flex items-center justify-center gap-2 bg-violet-500 hover:bg-violet-600 text-white text-sm font-semibold transition-colors active:scale-[0.97] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Send className="w-4 h-4" />
          {sending ? 'Enviando…' : stats?.announce_pending
            ? `Enviar anuncio de novedades (${stats.announce_pending})`
            : 'Anuncio ya enviado a todos'}
        </button>
      </div>

      <TableCard>
        <table className="w-full text-[13px]">
          <thead>
            <tr>
              <Th>Correo</Th>
              <Th>Fuente</Th>
              <Th>Dejó su correo</Th>
              <Th>Estado</Th>
              <Th>Bienvenida</Th>
              <Th>Anuncio novedades</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <TableMessage colSpan={6}>Cargando…</TableMessage>
            ) : items.length === 0 ? (
              <TableMessage colSpan={6}>Sin leads.</TableMessage>
            ) : items.map((l) => (
              <tr key={l.email} className={trClass}>
                <Td className="text-slate-200">{l.email}</Td>
                <Td className="text-xs text-slate-400">{l.source || '—'}</Td>
                <Td className="text-xs text-slate-400 whitespace-nowrap">{fmtDate(l.created_at)}</Td>
                <Td>
                  {l.converted ? <Tag tone="green" title={`Cuenta creada el ${fmtDate(l.account_created_at)}`}>Creó cuenta</Tag>
                    : l.unsubscribed ? <Tag tone="muted">Se dio de baja</Tag>
                    : <Tag tone="amber">Sin cuenta</Tag>}
                </Td>
                <Td>{l.welcome_email_sent ? <Tag tone="green">Enviada</Tag> : <Tag tone="muted">Pendiente</Tag>}</Td>
                <Td className="text-xs text-slate-400 whitespace-nowrap">
                  {l.announced_at ? <Tag tone="violet" title={fmtDateTime(l.announced_at)}>Enviado</Tag>
                    : l.converted ? <span className="text-slate-600">No aplica</span> : <span className="text-slate-600">—</span>}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableCard>

      <StickyPager show={total > LIMIT}>
        <Pagination page={page} pageCount={Math.max(1, Math.ceil(total / LIMIT))} onPageChange={setPage} total={total} pageSize={LIMIT} />
      </StickyPager>
    </div>
  );
}
